import { useCallback, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DndItemType } from '../types';
import { useEditSceneModalStateContext } from './EditSceneModalStateContext';
import { useSlots } from '.';

type SlotDragItem = {
  type: typeof DndItemType.SLOT;
  slotIndex: number;
};

type UseSlotSwapDndProps = {
  slotIndex: number;
};

export default function useSlotSwapDnd({ slotIndex }: UseSlotSwapDndProps) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const {
    dispatch,
    state: { controlPanelMode },
  } = useEditSceneModalStateContext();
  const { slots } = useSlots();
  const slot = slots?.[slotIndex];

  // Only allow dragging if slot has staticCrop and we're not in adjust-crop mode
  const canDrag = !!slot?.staticCrop && controlPanelMode !== 'adjust-crop';

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    item: {
      type: DndItemType.SLOT,
      slotIndex,
    } as SlotDragItem,
    canDrag,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const prevIsDragging = useRef(isDragging);

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: DndItemType.SLOT,
    drop: (item: SlotDragItem) => {
      if (item.slotIndex !== slotIndex && slot?.staticCrop) {
        dispatch({
          type: 'swap-slots',
          payload: {
            sourceIndex: item.slotIndex,
            targetIndex: slotIndex,
          },
        });
      }
    },
    canDrop: (item: SlotDragItem) => {
      return item.slotIndex !== slotIndex && !!slot?.staticCrop;
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      elementRef.current = el;
      dragRef(el);
      dropRef(el);
    },
    [dragRef, dropRef],
  );

  useEffect(() => {
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, [previewRef]);

  // select the current "source" slot when dragging starts.  the "select" action
  // fires on mouse down which won't happen until the user drops and probably won't
  // happen on the same element where dragging started
  useEffect(() => {
    const wasDragging = prevIsDragging.current;
    prevIsDragging.current = isDragging;

    if (!wasDragging && isDragging) {
      dispatch({ type: 'select-slot', payload: { slotIndex } });
    }
  }, [dispatch, isDragging, prevIsDragging, slotIndex]);

  return {
    ref,
    isDragging,
    isOver: isOver && canDrop,
  };
}
